/* istanbul ignore file */
import React, { ReactElement, useEffect } from "react";
import { PageRender } from "../page-render/page-render";
import { RapportageOnderdelen } from "../components/rapportage/rapportage-onderdelen";
import { usePagedReady } from "../contexts/paged-ready-context";
import { Previewer, registerHandlers } from "pagedjs";
import { ThemeProvider } from "styled-components";
import defaultTheme from "../styles/themes/default";
import { FixCanvasHandler } from "../handlers/fix-canvas-handler";
import { ReplaceFooterHandler } from "../handlers/replace-footer-handler";
import { ChapterProvider } from "../contexts/chapter-context";
import {
  RapportContextProvider,
  useRapportContext,
} from "../components/hoofdstukonderdelen/context/RapportContext";
import "./rapport-app.scss";
import { getHeaderLogo } from "./rapport-app-helper";

const RapportAppContent = (): ReactElement => {
  const { isPagedReady } = usePagedReady();
  const { logoUrl, footerText, hasFrontPage } = useRapportContext();

  useEffect(() => {
    const initPagedJS = async (): Promise<void> => {
      try {
        if (!isPagedReady) {
          return;
        }

        const content = document.querySelector(
          "#render-content"
        ) as HTMLElement;
        const preview = document.querySelector("#preview") as HTMLElement;

        if (!content || !preview) return;

        // Initialize PagedJS after all elements are present
        const paged = new Previewer();

        // Start PagedJS preview
        await paged
          .preview(
            content.innerHTML,
            [
              "/stylesheets/core.css",
              { "injected-css-blinqx-header": getHeaderLogo(logoUrl) },
            ],
            preview
          )
          .then(() => {
            const root = document.querySelector("#root");
            root?.remove();
          });
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error("Error initializing PagedJS:", error);
      }
    };

    // Configure and register handlers
    // Set static properties for the footer handler before Paged.js instantiates it
    ReplaceFooterHandler.footerText = footerText || "";

    registerHandlers(FixCanvasHandler);
    registerHandlers(ReplaceFooterHandler);

    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    initPagedJS();
  }, [isPagedReady, logoUrl, footerText, hasFrontPage]);

  return <RapportageOnderdelen />;
};

const RapportApp = (): ReactElement => {
  return (
    <PageRender>
      <ThemeProvider theme={defaultTheme}>
        <ChapterProvider>
          <RapportContextProvider>
            <RapportAppContent />
          </RapportContextProvider>
        </ChapterProvider>
      </ThemeProvider>
    </PageRender>
  );
};

RapportApp.displayName = "RapportApp";

export default RapportApp;
